import { AbstractControl, FormArray, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Product form rules shared by the create/edit dialog and the detail page, mirroring the backend
 * schema (libs product.dto): min/max/averageTime optional whole numbers >= 1, max >= min, tiers with
 * whole amounts, and no repeated provider + code.
 */

export const WHOLE_NUMBER_PATTERN = /^\d+$/;

/** Optional whole number >= 1 (empty is fine) */
export const optionalPositiveInt: ValidatorFn[] = [Validators.min(1), Validators.pattern(WHOLE_NUMBER_PATTERN)];

/** Required whole number >= 1 */
export const requiredPositiveInt: ValidatorFn[] = [
	Validators.required,
	Validators.min(1),
	Validators.pattern(WHOLE_NUMBER_PATTERN),
];

function isBlank(value: unknown): boolean {
	return value === null || value === undefined || value === '';
}

/** Group validator: maxAmount >= minAmount when both are set (the error is shown on max) */
export function maxNotBelowMinValidator(): ValidatorFn {
	return (group: AbstractControl): ValidationErrors | null => {
		const min = group.get('minAmount')?.value;
		const max = group.get('maxAmount')?.value;
		if (isBlank(min) || isBlank(max)) return null;
		return Number(max) < Number(min) ? { maxBelowMin: { min: Number(min) } } : null;
	};
}

/** FormArray validator: the same provider + code only once; lists the rows involved */
export function uniqueProviderCodeValidator(): ValidatorFn {
	return (array: AbstractControl): ValidationErrors | null => {
		const firstRow = new Map<string, number>();
		const indexes = new Set<number>();
		(array as FormArray).controls.forEach((row, i) => {
			const providerId = row.get('providerId')?.value;
			const code = String(row.get('code')?.value ?? '').trim();
			if (!providerId || !code) return;
			const key = `${providerId}:${code}`;
			const first = firstRow.get(key);
			if (first === undefined) {
				firstRow.set(key, i);
			} else {
				indexes.add(first);
				indexes.add(i);
			}
		});
		return indexes.size ? { duplicateProvider: { indexes: [...indexes] } } : null;
	};
}

/** Validation key + params of the first error, with `pattern` on a number shown as "whole number" */
export function firstError(errors: ValidationErrors | null | undefined): { key: string; params: unknown } | null {
	if (!errors) return null;
	const [key, params] = Object.entries(errors)[0];
	return key === 'pattern' ? { key: 'integer', params: null } : { key, params };
}
